import { Box, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import {
  MAX_PROFILE_NAME_LEN,
  PROFILE_PRICE_IN_MAFIA,
} from "../../constants/const";
import { Errors } from "../../constants/errors";
import { Messages } from "../../constants/messages";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useMafiaPrice from "../../hook/useMafiaPrice";
import {
  createProfileAction,
  getSwapTokenBalance,
  getSwapTokenInfo,
  getUserBoughtTicket,
  getUserTicketId,
  registerProfile,
  setIsCreating,
  signProfile,
} from "../../reducers/profile.slice";
import { toastError, toastSuccess } from "../../utils/utils";

import useWallet from "../../hook/useWallet";

import { CostInformation } from "./CostInformation";
import { CountrySelect } from "./CountrySelect";
import { CreateAccountButton } from "./CreateAccountButton";
import { GenderSelector } from "./GenderSelector";
import { useProfileCreation } from "./hook/useProfileCreation";
import { ProfileNameInput } from "./ProfileNameInput";

import TokenListPopup from "../TokenListPopup";
import useStyles from "./index.styles";

interface CreateProfileProps {
  showCreate: boolean;
  setShowCreate: () => void;
}

const CreateProfile = ({ showCreate, setShowCreate }: CreateProfileProps) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { account, connectWallet } = useWallet();

  const {
    isCreatingProfile,
    isLoadingProfileNames,
    profileNames,
    swapTokenInfo,
    swapTokenBalances,
  } = useAppSelector((store) => store.profile);

  const mafiaPrice = useMafiaPrice();
  const [alreadyCreated, setAlreadyCreated] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [selectedGender, setSelectedGender] = useState(0);
  const [country, setCountry] = useState("");
  const [openTokenListPopup, setOpenTokenListPopup] = useState(false);
  const [profilePriceUSD, setProfilePriceUSD] = useState(0);

  const {
    validateProfileName,
    checkDuplicateName,
    validationMessage,
    setValidationMessage,
  } = useProfileCreation();

  const selectGender = (gender: number) => {
    setSelectedGender(gender);
  };

  const checkDuplicated = (value: string) => {
    const duplicated =
      profileNames.find((profile, index) => profile.name === value) !==
      undefined;

    if (duplicated) {
      setValidationMessage("Duplicated Profile Name");
    }

    return duplicated;
  };

  const createSignMsg = (
    ticketId: number,
    country: string,
    address: string,
    gender: number
  ) => {
    const genderStr = gender === 0 ? "Male" : "Female";
    return {
      ticketId,
      country,
      address,
      gender: genderStr,
      domain: config.domain,
      uri: `https://${config.domain}`,
    };
  };

  const registerUserProfile = async ({
    signature,
    signMsg,
  }: {
    signature: string;
    signMsg: any;
  }) => {
    try {
      await dispatch(registerProfile({ signature, signMsg })).unwrap();

      toastSuccess(Messages.GLOBAL.ACCOUNT.CREATED_SUCCESS);
      setTimeout(() => {
        navigate(`/profile/${profileName}`);
      }, 3000);
    } catch (error) {
      toastError(Errors.GLOBAL.PROFILE.CREATION_FAILED);
      throw error;
    } finally {
      dispatch(setIsCreating(false));
    }
  };

  const handleProfileNameChange = async (event: any) => {
    let value = event.target.value;

    if (value.length === 0) {
      setProfileName(value);
      return;
    } else {
      value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

      const regex = /^[A-Z][a-z]*$/;

      if (value.length > MAX_PROFILE_NAME_LEN) {
        setValidationMessage(
          `Maximum Profile name length is ${MAX_PROFILE_NAME_LEN} letters`
        );
        return;
      }

      if (regex.test(value)) {
        setProfileName(value);
        if (checkDuplicated(value)) return;

        setValidationMessage("");
      } else {
        setValidationMessage("Profile name must consist only of letters.");
      }
    }
  };

  const handleGoProfile = () => {
    if (alreadyCreated) {
      const name = profileNames.find(
        (name, index) => name.address?.toLowerCase() === account.toLowerCase()
      )?.name;
      navigate(`/profile/${name}`);
    }
  };

  const handleCreateProfile = async () => {
    setShowCreate();
  };

  const handleCreateAccount = async () => {
    if (!account) return;

    const validationResult = validateProfileName(profileName, country);
    if (validationResult) {
      setValidationMessage(validationResult);
      return;
    }

    const isDuplicate = await checkDuplicateName(profileName);
    if (isDuplicate) {
      setValidationMessage("Profile name already exists");
      return;
    }

    setOpenTokenListPopup(true);
  };

  const confirmCreateAccount = async (swapTokenId: number) => {
    await handleProfileCreation(swapTokenId);
  };

  const handleProfileCreation = async (swapTokenId: number) => {
    let userTicketId = await dispatch(getUserTicketId(account)).unwrap();
    let bought = await dispatch(getUserBoughtTicket(account)).unwrap();

    // Register profile
    const registerProfile = () => {
      const signMsg = createSignMsg(
        userTicketId,
        country,
        account,
        selectedGender
      );

      dispatch(signProfile({ signMsg }))
        .unwrap()
        .then(async (signature) => {
          await registerUserProfile({ signature, signMsg });
        })
        .catch((error) => {
          console.log(error);
        });
    };

    // Purchase Ticket
    const createProfile = () => {
      const tokenAmount =
        (profilePriceUSD * 1.05) / swapTokenInfo[1][swapTokenId];

      dispatchTxAction(
        dispatch,
        createProfileAction({
          swapTokenId,
          tokenAmount,
          account,
          name: profileName,
          tokenAddress: swapTokenInfo[0][swapTokenId].tokenAddress,
        }),
        async () => {
          const checkTicket = async () => {
            userTicketId = await dispatch(getUserTicketId(account)).unwrap();
            if (userTicketId !== 0) {
              registerProfile();
            } else {
              setTimeout(checkTicket, 3000); // Retry after 3 seconds if still 0
            }
          };

          checkTicket();
        },
        3000
      );
    };

    // User hasn't purchased tickets, create profile first
    if (!bought) {
      createProfile();
    } else {
      registerProfile();
    }
  };

  useEffect(() => {
    const alreadyCreated =
      profileNames.find(
        (name, index) => name.address?.toLowerCase() === account.toLowerCase()
      ) !== undefined;

    setAlreadyCreated(alreadyCreated);
  }, [profileNames, account]);

  useEffect(() => {
    dispatch(getSwapTokenInfo());
    dispatch(getSwapTokenBalance({ account }));
  }, [dispatch, account]);

  useEffect(() => {
    setProfilePriceUSD(mafiaPrice * PROFILE_PRICE_IN_MAFIA);
  }, [mafiaPrice]);

  return (
    <Box className={classes.body}>
      {showCreate ? (
        <Box className={classes.createContainer}>
          <ProfileNameInput
            profileName={profileName}
            onChange={handleProfileNameChange}
            validationMessage={validationMessage}
          />

          <CountrySelect country={country} onChange={setCountry} />
          <GenderSelector
            selectedGender={selectedGender}
            onSelectGender={selectGender}
          />
          <CreateAccountButton
            isLoading={isCreatingProfile || isLoadingProfileNames}
            onClick={handleCreateAccount}
            disabled={!account}
          />
          <CostInformation mafiaPrice={mafiaPrice} />

          <TokenListPopup
            usdAmount={profilePriceUSD}
            swapTokenInfo={swapTokenInfo}
            swapTokenBalances={swapTokenBalances}
            openTokenListPopup={openTokenListPopup}
            setOpenTokenListPopup={setOpenTokenListPopup}
            handleTokenSelection={confirmCreateAccount}
          />
        </Box>
      ) : (
        <Box>
          <Button
            className={classes.startedBut}
            onClick={() => {
              if (!account) {
                connectWallet();
              } else if (alreadyCreated) {
                handleGoProfile();
              } else {
                handleCreateProfile();
              }
            }}
          >
            {!account
              ? "Connect Wallet"
              : alreadyCreated
              ? "Get Started!"
              : "Create Profile!"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CreateProfile;
